import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import IntegrationsHeader from '@/components/organisms/Headers/IntegrationsHeader';
import { AgentInfo } from '@/components/organisms/Integrations/Email/components/AgentInfo';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useIntegrations } from '@/hooks/useIntegrations';
import { selectAccountSlug } from '@/redux/session/selectors';
import { EmailIntegration, IntegrationType } from '@/shared/types/integration';

/**
 * EmailDraft - Draft page for creating new email integrations
 *
 * Features:
 * - Sender information form with email and name fields
 * - Character count validation (48 character limit)
 * - Email verification functionality
 *
 * @returns {JSX.Element} The email draft page component
 */
const EmailDraft = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { desk } = useDesks(deskId);

  useTitle(t('email.new_email', { 0: desk?.name ?? '-' }));
  const { createIntegration } = useIntegrations<EmailIntegration>(deskId);

  const slug = useSelector(selectAccountSlug);

  const navigate = useNavigate();

  const handleVerify = (data) => {
    // create integration with sender information
    createIntegration(
      {
        type: IntegrationType.EMAIL,
        name: 'email',
        desk_id: deskId,
        config: { from_email: data.from_email, from_name: data.from_name },
      },
      {
        onSuccess: ({ integration_id }) => {
          navigate(`/${slug}/environments/${deskId}/integrations/${integration_id}`, {
            replace: true,
          });
        },
      }
    );
  };

  return (
    <>
      <IntegrationsHeader />
      <PageContentWrapper>
        <div className="mx-auto flex max-w-5xl flex-col">
          <h3 className="mb-5 text-xl leading-none font-semibold">
            {t('email.sender_information')}
          </h3>
          <div className="max-w-5xl rounded-md rounded-xl bg-muted p-1">
            <AgentInfo onVerify={handleVerify} />
          </div>
        </div>
      </PageContentWrapper>
    </>
  );
};

export default EmailDraft;
